import { AgreementApiModel } from '../models/DealModel';

/**
 * Generates a printable label for zone restrictions based on the provided deal.
 *
 * @param {AgreementApiModel} deal - The deal object containing zone information.
 * @returns {string} - A formatted string representing the zone restrictions or 'N/A' if no restrictions are found.
 */
const printableZoneRestrictionLabel = (deal: AgreementApiModel): string => {
  // Mapping of country codes to their respective labels
  const countryLabels: { [key: string]: string } = {
    BR: 'Brazil',
    IN: 'India',
    US: 'United States',
  };

  // Filter and map zones to their respective country labels
  const countryRestriction =
    deal.zones && deal.zones.length > 0
      ? deal.zones
          .filter((zone) => zone.length > 1)
          .map((zone) => countryLabels[zone])
          .sort()
      : [];

  // Filter zones with a single character
  const zones: string[] = deal.zones.filter((zone) => zone.length === 1);

  if (zones.length === 0) {
    if (countryRestriction.length > 1) {
      // Format multiple country restrictions
      return `${countryRestriction
        .filter((_, i) => i < countryRestriction.length - 1)
        .join(', ')}  & ${countryRestriction[countryRestriction.length - 1]}`;
    }
    if (countryRestriction.length === 1) {
      // Return single country restriction
      return countryRestriction[0];
    }
  } else {
    let zonesRestrictions = '';
    // Format multiple zone restrictions
    if (zones.length > 1) {
      const last = zones.pop();
      const updatedZones = zones.length > 2 ? zones.slice(0, -1) : zones;
      // for more than 2 zone restrictions
      const cleanedZones = updatedZones.join(', ');
      zonesRestrictions = zones
        ? `Zones ${cleanedZones} ${last.length > 0 ? ` & ${last}` : ''}`
        : '';
    } else {
      zonesRestrictions = zones ? `Zone ${zones.join(', ')}` : '';
    }

    if (zonesRestrictions.length > 0 || countryRestriction.length > 0) {
      // Combine country and zone restrictions
      return `${
        countryRestriction.length > 0
          ? `${
              countryRestriction.length > 1
                ? countryRestriction.join(', ')
                : countryRestriction[0]
            } & `
          : ''
      }  ${
        zonesRestrictions.length > 0 ? `Countries in ${zonesRestrictions}` : ''
      }`;
    }
  }

  // Return 'N/A' if no restrictions are found
  return 'N/A';
};

/**
 * Generates a printable label for zones based on the provided deal.
 *
 * @param {AgreementApiModel|string[]} dealOrZones - The deal object containing zone information.
 * @returns {string} - A formatted string representing the zones or an empty string if no zones are found.
 */
export function printableZonesLabel(
  dealOrZones: AgreementApiModel | string[],
): string {
  // Filter zones with a single character
  const zones = !Array.isArray(dealOrZones)
    ? dealOrZones.zones.filter((zone) => zone.length === 1)
    : dealOrZones;

  let zonesRestrictions = '';
  if (zones.length > 0) {
    if (zones.length > 1) {
      // Format multiple zone restrictions
      zonesRestrictions = zones
        ? `Zones ${zones
            .filter((_, i) => i < zones.length - 1 || zones.length === 1)
            .join(', ')} ${
            zones.length > 2 ? ` & ${zones[zones.length - 1]}` : ''
          }`
        : '';
    } else {
      // Format single zone restriction
      zonesRestrictions = `Zone ${zones.join(',')}`;
    }
  }

  if (zonesRestrictions.length > 0) {
    // Return formatted zones
    return zonesRestrictions.length > 0
      ? `Countries in ${zonesRestrictions}`
      : '';
  }

  // Return an empty string if no zones are found
  return '';
}

export default printableZoneRestrictionLabel;
